package org.example.project.screens_sys

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.manager.LumaticaApiManager
import org.example.project.engineslot.SlotStyles
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private data class WebsitePageVisit(val page: String, val datum: String, val uhrzeit: String, val duration: String)
private data class WebsiteFlyerDownload(val name: String, val flyerId: String, val datum: String, val uhrzeit: String)
private data class WebsiteAnalytics(
    val sessions: List<WebsiteSession>
)
private data class WebsiteSession(
    val id: String,
    val datum: String,
    val uhrzeit: String,
    val ip: String,
    val geraet: String,
    val land: String,
    val einstieg: String,
    val dauer: String,
    val pages: List<WebsitePageVisit>,
    val flyers: List<WebsiteFlyerDownload>
)

private object WebsiteAnalyticsApi {
    private val json = Json { ignoreUnknownKeys = true }

    suspend fun load(): WebsiteAnalytics {
        val root = json.parseToJsonElement(LumaticaApiManager.call("webseitenuebersicht/sessions")).jsonObject
        val sessions = root["sessions"]?.jsonArray.orEmpty().mapNotNull { item ->
            val row = item.jsonObject
            WebsiteSession(
                id = row.text("id"),
                datum = row.text("datum"),
                uhrzeit = row.text("uhrzeit"),
                ip = row.text("ip"),
                geraet = row.text("geraet"),
                land = row.text("land", "–"),
                einstieg = row.text("einstieg"),
                dauer = row.text("dauer"),
                pages = row["seiten"]?.jsonArray.orEmpty().map { page ->
                    val detail = page.jsonObject
                    WebsitePageVisit(detail.text("page"), detail.text("datum"), detail.text("uhrzeit"), detail.text("duration"))
                },
                flyers = row["flyer"]?.jsonArray.orEmpty().map { flyer ->
                    val detail = flyer.jsonObject
                    WebsiteFlyerDownload(detail.text("name"), detail.text("flyerId", "Standard"), detail.text("datum"), detail.text("uhrzeit"))
                }
            )
        }
        return WebsiteAnalytics(sessions)
    }

    private fun JsonObject.text(key: String, fallback: String = ""): String =
        this[key]?.jsonPrimitive?.content ?: fallback
}

@Composable
fun SysWebseitenUebersicht() {
    val scope = rememberCoroutineScope()
    var sessions by remember { mutableStateOf<List<WebsiteSession>>(emptyList()) }
    var selectedSessionId by remember { mutableStateOf<String?>(null) }
    var loadError by remember { mutableStateOf(false) }

    suspend fun reload() {
        val result = try { WebsiteAnalyticsApi.load() } catch (error: Exception) {
            if (error is kotlinx.coroutines.CancellationException) throw error
            loadError = true
            return
        }
        sessions = result.sessions
        loadError = false
        if (selectedSessionId !in sessions.map { it.id }.toSet()) selectedSessionId = null
    }

    LaunchedEffect(Unit) {
        while (true) {
            reload()
            delay(30_000)
        }
    }

    Style(SlotStyles)
    Div({ classes(SlotStyles.screenRoot) }) {
    if (loadError) Div { Text("Besuchsdaten konnten nicht geladen werden.") }
    val selected = sessions.firstOrNull { it.id == selectedSessionId }
    TabularSplitScreen(
      screen1 = {
      Div({ style { flex("1"); minHeight(0.px); overflow("hidden") } }) {
      TabularScreen(
        headers = listOf("Datum", "Uhrzeit", "IP-Adresse", "Gerät", "Seitenadresse", "Seiten", "Flyer Downloads", "Sitzungsdauer"),
        data = sessions.map { session ->
            session.id to listOf(session.datum, session.uhrzeit, session.ip, session.geraet, session.einstieg, session.pages.size.toString(), session.flyers.size.toString(), formatWebsiteDuration(session.dauer))
        },
        screenKey = "WebseitenUebersicht",
        title = "Webseitenübersicht",
        editable = false,
        hardReadOnly = true,
        includeMetadata = false,
        allowCreate = false,
        allowSave = false,
        allowDelete = false,
        allowImportButton = false,
        allowExportButton = true,
        allowSettingsButton = true,
        allowRefreshButton = true,
        onRefresh = { scope.launch { reload() } },
        onRowClick = { selectedSessionId = it },
        onCommit = {},
        dataColumnWidthCss = "120px 100px minmax(140px, 1fr) 100px minmax(310px, 2fr) 80px 130px 150px"
    )

      }
      },
      screen2 = selected?.let { selectedVisit -> {
        TabularSplitScreen(
          initialRatio = 0.58f,
          minRatio = 0.25f,
          maxRatio = 0.8f,
          screen1 = {
        Div({ style { flex("1"); minHeight(0.px); overflow("hidden") } }) {
        TabularScreen(
            headers = listOf("Datum", "Uhrzeit", "Seitenadresse", "Seitendauer"),
            data = selectedVisit.pages.mapIndexed { index, page ->
                "${selectedVisit.id}-$index" to listOf(page.datum, page.uhrzeit, page.page, formatWebsiteDuration(page.duration))
            },
            screenKey = "WebseitenUebersichtDetail",
            title = "Seitenfolge",
            editable = false,
            hardReadOnly = true,
            includeMetadata = false,
            allowCreate = false,
            allowSave = false,
            allowDelete = false,
            allowImportButton = false,
            allowExportButton = false,
            allowSettingsButton = false,
            allowRefreshButton = false,
            showFilterRow = false,
            showFillerRows = false,
            onCommit = {},
            dataColumnWidthCss = "120px 100px minmax(260px, 1fr) 130px"
        )
        }
        },
        screen2 = {
        Div({ style { flex("1"); minHeight(0.px); overflow("hidden") } }) {
        TabularScreen(
            headers = listOf("Flyername", "Flyer-ID", "Download am", "Download um"),
            data = selectedVisit.flyers.mapIndexed { index, flyer ->
                "${selectedVisit.id}-flyer-$index" to listOf(flyer.name, flyer.flyerId, flyer.datum, flyer.uhrzeit)
            },
            screenKey = "WebseitenUebersichtProspekte",
            title = "Flyer geladen · ${selectedVisit.flyers.size}",
            editable = false,
            hardReadOnly = true,
            includeMetadata = false,
            allowCreate = false,
            allowSave = false,
            allowDelete = false,
            allowImportButton = false,
            allowExportButton = false,
            allowSettingsButton = false,
            allowRefreshButton = false,
            showFilterRow = false,
            showFillerRows = false,
            onCommit = {},
            dataColumnWidthCss = "minmax(280px, 1fr) 220px 130px 100px"
        )
        }
        }
      )
      }}
    )
    }
}
